import React from 'react';
import { Alert, Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { useAtom } from 'jotai';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import { completenessModalPackageIdAtom } from '../../../atoms/packager-atoms';
import { useGetPackage } from '../../hooks/packages/useGetPackage';
import { PackageCompleteness, PackageStatuses } from '../../../types/packager-types';
import CompletenessFunnel from './CompletenessFunnel';
import ValidationSummary from './ValidationSummary';

// The plain-language verdict sentence — the answer first. Driven by health; the
// client only assembles the sentence, never derives the health.
const verdict = (
  completeness: PackageCompleteness | null,
  status?: PackageStatuses
): { severity: 'success' | 'error' | 'info'; message: string } => {
  if (completeness === null) {
    // Completeness is computed when the package finishes building, so a package still
    // in flight legitimately has no record *yet* — that's not the same as a legacy
    // package built before the feature existed. Branch on status so a PROCESSING
    // package doesn't get mislabeled "built before this feature".
    if (status === PackageStatuses.CREATED || status === PackageStatuses.PROCESSING) {
      return {
        severity: 'info',
        message: 'Completeness will be available once the package finishes processing.',
      };
    }
    if (status === PackageStatuses.PROCESSING_FAILED) {
      return {
        severity: 'info',
        message: "Completeness wasn't computed because the package failed to process.",
      };
    }
    return {
      severity: 'info',
      message: "Completeness wasn't recorded for this package (built before this feature).",
    };
  }
  if (completeness.health === 'COMPLETE') {
    return {
      severity: 'success',
      message: `Complete — all ${completeness.documents_delivered.toLocaleString()} documents we kept were delivered.`,
    };
  }
  const lost = completeness.documents_to_deliver - completeness.documents_delivered;
  return {
    severity: 'error',
    message: `Incomplete — ${lost.toLocaleString()} ${
      lost === 1 ? 'document was' : 'documents were'
    } kept for delivery but didn't make it into the file.`,
  };
};

const CompletenessModal: React.FC = () => {
  const theme = useTheme();
  const [packageId, setPackageId] = useAtom(completenessModalPackageIdAtom);

  const { data: pkg, isLoading, isError } = useGetPackage(packageId || undefined);

  const closeModal = () => setPackageId(null);

  const completeness = pkg?.completeness ?? null;
  const { severity, message } = verdict(completeness, pkg?.status);

  return (
    <DialogGlynt
      open={!!packageId}
      closeModal={closeModal}
      cypressId="dialog-package-completeness"
      width="640px"
    >
      <DialogTitleGlynt title="Package Completeness" closeModal={closeModal} />
      <DialogContentGlynt>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : isError ? (
          <Alert severity="error" data-cy="completeness-load-error">
            Sorry, something went wrong loading package completeness.
          </Alert>
        ) : (
          <Box sx={{ pt: 0.5 }}>
            <Alert severity={severity} data-cy="completeness-verdict">
              {message}
            </Alert>

            {completeness && packageId && (
              <CompletenessFunnel completeness={completeness} packageId={packageId} />
            )}

            {pkg?.validation && packageId && (
              <ValidationSummary validation={pkg.validation} packageId={packageId} />
            )}

            {completeness && (
              <Typography
                sx={{
                  fontSize: '11px',
                  color: theme.palette.text.secondary,
                  mt: 2,
                  fontStyle: 'italic',
                }}
              >
                Measured when the package was built ·{' '}
                {new Date(completeness.computed_at).toLocaleString()}
              </Typography>
            )}
          </Box>
        )}
      </DialogContentGlynt>
    </DialogGlynt>
  );
};

export default CompletenessModal;
